/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Stack, styled } from '@mui/material';
import TabHeader from './TabHeader';
import DeliveryAddress from './DeliveryAddress';
import { SaleAgreementPromo } from './types';
import PickupSite from './PickupSite';
import { colors } from '@farmlands/mui-theme';
import MaterialSymbol from '../../../../components/MaterialSymbol';

import { Tabs as BaseTabs } from '@mui/base/Tabs';
import { Tab as BaseTab } from '@mui/base/Tab';
import { TabsList as BaseTabsList } from '@mui/base/TabsList';
import { TabPanel as BaseTabPanel } from '@mui/base/TabPanel';
import { useCampaignContext } from './CampaignContext';

interface SearchAddressTabProps {
  config: SaleAgreementPromo;
}

const SearchAddressTab = ({ config }: SearchAddressTabProps) => {
  return (
    <Stack direction='column' gap={2}>
      <TabHeader
        title='Delivery address'
        caption='Please confirm your delivery address so we can include any freight costs'
      />
      <DeliveryMethods config={config} />
    </Stack>
  );
};

interface DeliveryMethodsProps {
  config: SaleAgreementPromo;
}

const DeliveryMethods = ({ config }: DeliveryMethodsProps) => {
  const { state, setState } = useCampaignContext();
  const { deliveryOptions } = config;
  const currentTabIndex = deliveryOptions.findIndex((d) => {
    return d.type === state.deliveryOption?.type;
  });

  function handleTabChange(value: number) {
    setState({
      deliveryOption: deliveryOptions[value],
    });
  }

  if (deliveryOptions.length === 1 && deliveryOptions[0].type === 'Delivery') {
    return <DeliveryAddress />;
  }

  if (deliveryOptions.length === 1 && deliveryOptions[0].type === 'Pickup') {
    return <PickupSite config={config} />;
  }

  return (
    <Stack direction='column'>
      <BaseTabs value={currentTabIndex} onChange={(_, val) => handleTabChange(Number(val))}>
        <StyledBaseTabsList>
          {deliveryOptions.map((deliveryOption, index) => {
            return (
              <StyledTab value={index} key={deliveryOption.type}>
                <DeliveryMethodIcon type={deliveryOption.type} />
                {deliveryOption.type}
              </StyledTab>
            );
          })}
        </StyledBaseTabsList>
        {deliveryOptions.map((deliveryOption, index) => {
          switch (deliveryOption.type) {
            case 'Delivery':
              return (
                <BaseTabPanel value={index} key={'delivery'}>
                  <DeliveryAddress />
                </BaseTabPanel>
              );
            case 'Pickup':
              return (
                <BaseTabPanel value={index} key={'pickup'}>
                  <PickupSite config={config} />
                </BaseTabPanel>
              );
            default:
              return null;
          }
        })}
      </BaseTabs>
    </Stack>
  );
};

interface DeliveryMethodIconProps {
  type: 'Delivery' | 'Pickup';
}

const DeliveryMethodIcon = ({ type }: DeliveryMethodIconProps) => {
  switch (type) {
    case 'Delivery':
      return <MaterialSymbol sx={{ fontWeight: 400 }}>local_shipping</MaterialSymbol>;
    case 'Pickup':
      return <MaterialSymbol sx={{ fontWeight: 400 }}>location_on</MaterialSymbol>;
    default:
      return null;
  }
};

const StyledTab = styled(BaseTab)(({ theme }) => ({
  ...theme.typography.body2,
  width: '200px',
  paddingTop: '12px',
  paddingBottom: '12px',
  paddingRight: '16px',
  paddingLeft: '16px',
  display: 'flex',
  justifyContent: 'center',
  alignItem: 'center',
  gap: '8px',
  [theme.breakpoints.down('sm')]: {
    flex: '1 1',
  },
  backgroundColor: colors.primary25,
  '&:hover': {
    backgroundColor: colors.primary100,
  },
  '&.base--selected': {
    backgroundColor: colors.primary100,
    fontWeight: 700,
  },
  borderRadius: '50px',
  border: 0,
  color: colors.primary500,
}));

const StyledBaseTabsList = styled(BaseTabsList)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  marginBottom: '24px',
}));

export default SearchAddressTab;
